import { useReducer } from 'react';
import { LayoutDefinition } from '../../../../infra/protected/Layout/LayoutDefinitions';
import { FPAData } from '../../../../infra/protected/FPA/FPAData';
import { ActivityItem, PageView } from '../types';

// State interface for useReducer
export interface ServiceActivitiesState {
  showLoader: boolean;
  showLoaderDetail: boolean;
  rowsData: ActivityItem[];
  pageView: PageView;
  selectedKeys: Set<string | number> | undefined;
  definition: LayoutDefinition | null;
  showMobileDetailView: boolean;
  selectedItem: FPAData | null;
  selectedDataRow: ActivityItem | null;
  showAdd: boolean;
  isMoveDialogOpen: boolean;
  isDeleteDialogOpen: boolean;
  showFilter: boolean;
}

// Action types
export type ServiceActivitiesAction =
  | { type: 'SET_SHOW_LOADER'; payload: boolean }
  | { type: 'SET_SHOW_LOADER_DETAIL'; payload: boolean }
  | { type: 'SET_ROWS_DATA'; payload: ActivityItem[] }
  | { type: 'APPEND_ROWS_DATA'; payload: ActivityItem[] }
  | { type: 'SET_PAGE_VIEW'; payload: PageView }
  | { type: 'SET_SELECTED_KEYS'; payload: Set<string | number> | undefined }
  | { type: 'SET_DEFINITION'; payload: LayoutDefinition | null }
  | { type: 'SET_SHOW_MOBILE_DETAIL_VIEW'; payload: boolean }
  | { type: 'SET_SELECTED_ITEM'; payload: FPAData | null }
  | { type: 'SET_SELECTED_DATA_ROW'; payload: ActivityItem | null }
  | { type: 'SET_SHOW_ADD'; payload: boolean }
  | { type: 'SET_IS_MOVE_DIALOG_OPEN'; payload: boolean }
  | { type: 'SET_IS_DELETE_DIALOG_OPEN'; payload: boolean }
  | { type: 'SET_SHOW_FILTER'; payload: boolean }
  | { type: 'RESET_SELECTION' }
  | { type: 'RESET_STATE' };

// Initial state
const initialState: ServiceActivitiesState = {
  showLoader: true,
  showLoaderDetail: true,
  rowsData: [],
  pageView: 'Detail',
  selectedKeys: undefined,
  definition: null,
  showMobileDetailView: false,
  selectedItem: null,
  selectedDataRow: null,
  showAdd: false,
  isMoveDialogOpen: false,
  isDeleteDialogOpen: false,
  showFilter: false,
};

// Reducer function
function serviceActivitiesReducer(state: ServiceActivitiesState, action: ServiceActivitiesAction): ServiceActivitiesState {
  switch (action.type) {
    case 'SET_SHOW_LOADER':
      return { ...state, showLoader: action.payload };
    case 'SET_SHOW_LOADER_DETAIL':
      return { ...state, showLoaderDetail: action.payload };
    case 'SET_ROWS_DATA':
      return { ...state, rowsData: action.payload };
    case 'APPEND_ROWS_DATA':
      return { ...state, rowsData: [...state.rowsData, ...action.payload] };
    case 'SET_PAGE_VIEW':
      return { ...state, pageView: action.payload };
    case 'SET_SELECTED_KEYS':
      return { ...state, selectedKeys: action.payload };
    case 'SET_DEFINITION':
      return { ...state, definition: action.payload };
    case 'SET_SHOW_MOBILE_DETAIL_VIEW':
      return { ...state, showMobileDetailView: action.payload };
    case 'SET_SELECTED_ITEM':
      return { ...state, selectedItem: action.payload };
    case 'SET_SELECTED_DATA_ROW':
      return { ...state, selectedDataRow: action.payload };
    case 'SET_SHOW_ADD':
      return { ...state, showAdd: action.payload };
    case 'SET_IS_MOVE_DIALOG_OPEN':
      return { ...state, isMoveDialogOpen: action.payload };
    case 'SET_IS_DELETE_DIALOG_OPEN':
      return { ...state, isDeleteDialogOpen: action.payload };
    case 'SET_SHOW_FILTER':
      return { ...state, showFilter: action.payload };
    case 'RESET_SELECTION':
      return {
        ...state,
        selectedKeys: undefined,
        selectedItem: null,
        selectedDataRow: null,
        definition: null,
      };
    case 'RESET_STATE':
      return { ...initialState };
    default:
      return state;
  }
}

// Custom hook to manage ServiceActivities state
export function useServiceActivitiesState() {
  const [state, dispatch] = useReducer(serviceActivitiesReducer, initialState);

  // Action creators
  const actions = {
    setShowLoader: (payload: boolean): void => dispatch({ type: 'SET_SHOW_LOADER', payload }),
    setShowLoaderDetail: (payload: boolean): void => dispatch({ type: 'SET_SHOW_LOADER_DETAIL', payload }),
    setRowsData: (payload: ActivityItem[]): void => dispatch({ type: 'SET_ROWS_DATA', payload }),
    appendRowsData: (payload: ActivityItem[]): void => dispatch({ type: 'APPEND_ROWS_DATA', payload }),
    setPageView: (payload: PageView): void => dispatch({ type: 'SET_PAGE_VIEW', payload }),
    setSelectedKeys: (payload: Set<string | number> | undefined): void => dispatch({ type: 'SET_SELECTED_KEYS', payload }),
    setDefinition: (payload: LayoutDefinition | null): void => dispatch({ type: 'SET_DEFINITION', payload }),
    setShowMobileDetailView: (payload: boolean): void => dispatch({ type: 'SET_SHOW_MOBILE_DETAIL_VIEW', payload }),
    setSelectedItem: (payload: FPAData | null): void => dispatch({ type: 'SET_SELECTED_ITEM', payload }),
    setSelectedDataRow: (payload: ActivityItem | null): void => dispatch({ type: 'SET_SELECTED_DATA_ROW', payload }),
    setShowAdd: (payload: boolean): void => dispatch({ type: 'SET_SHOW_ADD', payload }),
    setIsMoveDialogOpen: (payload: boolean): void => dispatch({ type: 'SET_IS_MOVE_DIALOG_OPEN', payload }),
    setIsDeleteDialogOpen: (payload: boolean): void => dispatch({ type: 'SET_IS_DELETE_DIALOG_OPEN', payload }),
    setShowFilter: (payload: boolean): void => dispatch({ type: 'SET_SHOW_FILTER', payload }),
    resetSelection: (): void => dispatch({ type: 'RESET_SELECTION' }),
    resetState: (): void => dispatch({ type: 'RESET_STATE' }),
  };

  return {
    state,
    dispatch,
    actions,
  };
}
